/**
 * Gabarits des e-mails sortants (document 09 § 11).
 *
 * Un gabarit est un texte libre dans lequel l'utilisateur insère des jetons `{{variable}}`.
 * Les jetons sont remplacés au moment de l'envoi par les valeurs réelles du contexte
 * (date du jour, tiers concerné, utilisateur expéditeur…).
 *
 * Aucun gabarit défini ⇒ l'application utilise son message par défaut : la personnalisation
 * est purement additive, elle ne peut jamais dégrader un envoi existant.
 */
/** Types d'e-mails dont le texte est personnalisable. */
export const MAIL_TEMPLATE_KINDS = ['quote', 'order', 'invoice', 'appointment'];
/** Libellés des types de gabarits, présentés dans l'écran des paramètres e-mail. */
export const MAIL_TEMPLATE_KIND_LABELS = {
    quote: 'Devis',
    order: 'Commande',
    invoice: 'Facture',
    appointment: 'Confirmation de rendez-vous',
};
/** Description du contexte d'envoi de chaque gabarit. */
export const MAIL_TEMPLATE_KIND_DESCRIPTIONS = {
    quote: "E-mail accompagnant l'envoi d'un devis au client ou au prospect.",
    order: "E-mail accompagnant l'envoi d'une commande au client.",
    invoice: "E-mail accompagnant l'envoi d'une facture au client.",
    appointment: "E-mail de confirmation envoyé aux participants d'un rendez-vous.",
};
/** Variables communes aux documents commerciaux (devis, commande, facture). */
const DOCUMENT_VARIABLES = [
    { token: 'date', label: 'Date du jour', sample: '29/10/2026' },
    { token: 'client', label: 'Client ou prospect', sample: 'SCI LES TILLEULS' },
    { token: 'utilisateur', label: 'Utilisateur (expéditeur)', sample: 'Jean DUPONT' },
    { token: 'societe', label: 'Société', sample: 'NORYVOX' },
    { token: 'numero', label: 'Numéro du document', sample: 'DEV-2026-0042' },
    { token: 'montant', label: 'Montant total', sample: '4 250,00 €' },
];
/** Variables propres à la confirmation de rendez-vous. */
const APPOINTMENT_VARIABLES = [
    { token: 'date', label: 'Date du jour', sample: '29/10/2026' },
    {
        token: 'creneau',
        label: 'Créneau du rendez-vous',
        sample: 'le vendredi 30 octobre 2026 de 14:30 à 15:30',
    },
    {
        token: 'dateheure',
        label: 'Date et heure du rendez-vous',
        sample: 'le vendredi 30 octobre 2026 à 14:30',
    },
    { token: 'client', label: 'Client ou prospect du rendez-vous', sample: 'SCI LES TILLEULS' },
    { token: 'utilisateur', label: 'Utilisateur (expéditeur)', sample: 'Jean DUPONT' },
    { token: 'objet', label: 'Objet du rendez-vous', sample: 'Visite technique' },
    { token: 'lieu', label: 'Lieu', sample: '12 rue des Lilas, 67000 Strasbourg' },
    { token: 'description', label: 'Description', sample: 'Relevé de cotes avant chiffrage.' },
    { token: 'societe', label: 'Société', sample: 'NORYVOX' },
];
/** Variables proposées pour chaque type d'e-mail, dans l'ordre d'affichage des boutons. */
export const MAIL_TEMPLATE_VARIABLES = {
    quote: DOCUMENT_VARIABLES,
    order: DOCUMENT_VARIABLES,
    invoice: DOCUMENT_VARIABLES,
    appointment: APPOINTMENT_VARIABLES,
};
/** Motif de reconnaissance d'un jeton (`{{token}}`), tolérant aux espaces internes. */
const TEMPLATE_TOKEN_PATTERN = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;
/**
 * Remplace les jetons d'un gabarit par leurs valeurs.
 * Un jeton sans valeur connue est laissé tel quel : la lacune reste visible plutôt que de
 * produire silencieusement un texte amputé.
 */
export function renderMailTemplate(template, values) {
    return template.replace(TEMPLATE_TOKEN_PATTERN, (match, token) => {
        const value = values[token.toLowerCase()];
        return value === undefined ? match : value;
    });
}
/** Valeurs d'exemple d'un type de gabarit, utilisées pour l'aperçu dans l'interface. */
export function mailTemplateSampleValues(kind) {
    return Object.fromEntries(MAIL_TEMPLATE_VARIABLES[kind].map((variable) => [variable.token, variable.sample]));
}
//# sourceMappingURL=mail-template.constants.js.map